




















































































































































































import Vue from 'vue'
import LeftSidebarLayout from '@/components/LeftSidebarLayout'
import InviteTeamMember from '@/components/InviteTeamMember'
import SelfTestSummary from '@/components/SelfTestSummary'
import Modal from '@/components/Modal'
import UserProfileForm from '@/components/UserProfileForm'
import roleNames from '@/roleNames'
import TabDisplay from '@/components/TabDisplay'
import { Table, TableColumn, Button, Input } from 'element-ui'
import { sortBy, startCase, debounce } from 'lodash'
import fb from '@/firebase'
import { UserProfile } from '@/types/users'
import { MemberInvite, MemberRole, TeamGroup } from '@/types/teams'
import { KeyDict } from '@/types'
import { TranscodingRegion } from '@/types/admin'
import { AbilityAction, AbilitySubject } from '@/abilitiesByRole'
import './MemberManagement.scss'
import { Feature } from '@/featureFlags/featureFlags'

export default Vue.extend({
  name: 'MemberManagement',
  computed: {
    canCreateMember (): boolean {
      return this.$can(AbilityAction.Create, AbilitySubject.Members)
    },
    canEditMember (): boolean {
      return this.$can(AbilityAction.Edit, AbilitySubject.Members)
    },
    canDoOps (): boolean {
      return this.$can(AbilityAction.Do, AbilitySubject.Ops)
    },
    canReadTeam (): boolean {
      return this.$can(AbilityAction.Read, AbilitySubject.Members)
    },
    activeTeam (): string {
      return this.$store.direct.state.team.activeTeam
    },
    currentTeam (): UserProfile[] | undefined {
      return this.$store.direct.state.team.currentTeam
    },
    filteredTeam (): UserProfile[] | undefined {
      //Not supporting fuzzy search at this time
      if (this.searchQueryDebounced !== '') { //Only filter when query entered
        const query = this.searchQueryDebounced.trim().toLowerCase() //Force matching case
        //Filter list to those matching query
        const filteredTeam = this.currentTeam?.filter((user: UserProfile) => {
          if (user.anonymous) return false
          //Create name term to search against
          const bidirFullName = user.first_name + ' ' + user.last_name 
          + ' ' + user.first_name //Support bi-directional name query
          //Search against full name and email
          if (bidirFullName.toLowerCase().indexOf(query) !== -1 ||
           user?.email?.toLowerCase().indexOf(query) !== -1) {
            return true
          }
          return false //No match
        })
        return filteredTeam
      }
      return this.currentTeam //Default to full list if no query
    },
    teamGroups (): TeamGroup[] {
      return this.$store.direct.state.team.teamGroups ?? []
    },
    invites (): MemberInvite[] {
      return this.$store.direct.state.team.invites
    },
    currentUserId (): string {
      return this.$store.direct.state.user.id
    },
    teamCheck (): boolean | null {
      return this.$store.direct.state.user.teamCheck
    },
    isSelfServe (): boolean {
      return this.$hasFeature(Feature.SELF_SERVE) ?? false
    },
    transcodingregions (): KeyDict<TranscodingRegion> {
      return this.$store.direct.state.admin.transcodingregions
    },
    currentTeamTable (): UserProfile[] {
      const teamTable: UserProfile[] = []
      for (const teamidx in this.filteredTeam) {
        const profile = this.filteredTeam[teamidx]
        if (profile.anonymous) continue
        const t: UserProfile = { ...profile, roleText: startCase(roleNames(this.filteredTeam[teamidx].role) as string) }
        t.first_name = startCase(t.first_name)
        t.last_name = startCase(t.last_name)
        t.transcodingregiontext = t.transcodingregion
        if (t.transcodingregiontext && t.transcodingregiontext in this.transcodingregions) {
          t.transcodingregiontext = this.transcodingregions[t.transcodingregiontext].name
        }
        t.transcodingregiontext = startCase(t.transcodingregiontext)
        //Generate sorted list of assigned groups that can be sorted in table column
        if (t.teamGroups?.hasOwnProperty(this.activeTeam)) {
          t.sortedTeamGroupNames = this.getAssignedGroupNames(t.teamGroups[this.activeTeam]).sort()
        } else t.sortedTeamGroupNames = ['All']
        teamTable.push(t)
      }
      return teamTable
    },
    sortedInvites (): MemberInvite[] {
      let openInvited = this.invites.filter(invite => invite.signup_complete !== true)
      openInvited = openInvited.map(invite => { return {...invite, role: startCase(roleNames(invite.role) as string) as MemberRole } })
      return sortBy(openInvited, 'email')
    },
    canDelete (): boolean {
      return this.$can(AbilityAction.Delete, AbilitySubject.Members)
    }
  },
  data: () => ({
    showModal: false,
    inviteModal: false,
    selectedMember: {},
    searchQuery: '',
    searchQueryDebounced: '',
    activeTab: 'active',
    tabs: [
      { label: 'Active', value: 'active', icon: '' },
      { label: 'Pending Invites', value: 'invites', icon: '' }
    ]
  }),
  methods: {
    inviteMember (): void {
      this.selectedMember = {}
      this.inviteModal = true
    },
    async inviteTeamMember (data) {
      const inviteData = JSON.parse(JSON.stringify(data))
      inviteData.team_id = this.activeTeam
      inviteData.sent_from = this.currentUserId
      inviteData.email = inviteData.email.toLowerCase()
      const handleInvite = fb.functions.httpsCallable('handleInvite', inviteData)
      const inviteResponse = await handleInvite(inviteData)
      if (inviteResponse) {
        switch(inviteResponse.data.status) {
          case 'invite_created':
            this.inviteModal = false
            this.$message({ type: 'success', message: 'Invite has been sent', duration: 6000 })
            break
          case 'user_already_on_team':
            this.$message({ type: 'warning', message: 'The user you are trying to invite is already on your team.', duration: 6000 })
            break
          case 'user_failed_to_be_added':
            this.$message({ type: 'warning', message: 'User failed to be added to your team. Please contact support.', duration: 6000 })
            break
          case 'user_failed_multiple_profiles':
            this.$message({ type: 'warning', message: 'User failed to be added to your team because of multiple profiles. Please contact support.', duration: 6000 })
            break
          case 'invalid_invite':
            this.$message({ type: 'warning', message: 'User failed to be added due to invalid invite data. Please contact support.', duration: 6000 })
            break
          default:
            this.$message({ type: 'warning', message: 'Error sending invite.', duration: 6000 })
        }
      } else {
        this.$message({ type: 'warning', message: 'Error sending invite.', duration: 6000 })
      }
    },
    editMember (member: MemberInvite): void {
      this.selectedMember = Object.assign({}, member)
      this.showModal = true
    },
    updateTeamMember (): void {
      const updatedMember = JSON.parse(JSON.stringify(this.selectedMember))
      updatedMember.teamid = this.activeTeam
      this.$store.direct.dispatch.team.updateTeamMember(updatedMember).then(() => {
        // this.$store.dispatch('subscribeCurrentTeam', this.activeTeam)
        this.showModal = false
        this.selectedMember = {}
        this.$message({
          type: 'success',
          message: 'User updated'
        })
        this.$forceUpdate() //Update 'Groups' column in table to reflect new values
      }).catch(error => {
        console.error('Error updateTeamMember', error)
      })
    },
    removeMember (member: MemberInvite): void {
      this.$confirm('Are you sure you want to delete this member?', 'Warning', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.$store.direct.dispatch.team.removeMemberFromTeam({ teamId: this.activeTeam, ...member }).then(() => {
          // this.$store.dispatch('getCurrentTeam', this.activeTeam)
          this.$message({
            type: 'success',
            message: 'Member deleted'
          })
        }).catch(error => {
          console.error('Error removeMember', error)
        })
      }).catch(error => {
        console.log('remove member cancelled', error)
      })
    },
    deleteInvite (invite: MemberInvite): void {
      console.log(invite)
      this.$confirm('Are you sure you want to delete this invite?', 'Warning', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.$store.direct.dispatch.team.deleteInvite(invite)
        this.$message({
          type: 'success',
          message: 'Invite deleted'
        })
      }).catch(error => {
        // dont write in sentry if the person clicked the cancel button
        console.warn('Warning deleteInvite', error)
      })
    },
    resendInvite (invite): void {
      this.$store.direct.dispatch.team.resendInvite(invite)
      this.$message({
        type: 'success',
        message: 'Invite resent'
      })
    },
    getInviteLink (invite: MemberInvite): string {
      return `${window.location.origin}/invite/${invite.id}`
    },
    tabChanged (tab): void {
      this.activeTab = tab
    },
    closeModal (): void {
      this.inviteModal = false
      this.showModal = false
    },
    debounceSearchQuery: debounce(function (this: any, value: string): void {
      this.searchQueryDebounced = value
    }, 200),
    getAssignedGroupNames (groupIds: string[]) {
      if (!groupIds.length) return ['All'] //Default for empty lists
      const groupNames: string[] = []
      for (const group of groupIds) {
        groupNames.push(this.teamGroups.find(teamGroup => teamGroup.id === group)?.name ?? 'All')
      }
      return groupNames
    }
  },
  components: {
    LeftSidebarLayout,
    InviteTeamMember,
    UserProfileForm,
    Modal,
    TabDisplay,
    SelfTestSummary,
    'el-table': Table,
    'el-table-column': TableColumn,
    'el-button': Button,
    'el-input': Input,
  }
})
